/** CVD and Open Interest panes for GC futures.
 *
 *  CVD — anchored, never a running total from the start of all data. The API
 *  serves per-bar delta; this cumulates it inside the chosen anchor window
 *  (session / day / whole visible range), so the number always answers "net flow
 *  since <a moment you picked>". An unanchored total answers nothing.
 *
 *  Contracts are separate series and are never cumulated across. GCQ6 and GCZ6
 *  are different order books; splicing their delta would invent flow that no one
 *  traded. The contract switch is explicit for that reason.
 *
 *  CVD is also SPARSE — only the dates whose ticks were bought exist. Outside
 *  them the pane draws nothing rather than zero-filling, because a flat line at
 *  zero reads as balanced two-way flow, which is a claim about the market rather
 *  than an absence of data.
 *
 *  OI — daily, settlement-derived, and total across every expiry. Per-contract OI
 *  collapses to zero at each expiry and looks like a market exodus when it is
 *  only a roll. Two renderings: an absolute line (a slow trend, NOT an
 *  oscillator, so no bounded range) and a change histogram coloured by the
 *  price/OI quadrant, which is where the read actually is.
 */
import { useEffect, useMemo, useState } from "react";

import { api } from "@/api/client";
import type { GCDeltaBar, GCOIPoint } from "@/api/types";
import { TimeSeriesPane, type PanePoint } from "./TimeSeriesPane";
import styles from "./GCPanels.module.css";

const CONTRACTS = ["GCZ6", "GCQ6"] as const;
type Contract = (typeof CONTRACTS)[number];
type Anchor = "session" | "day" | "range";

/** CME trade date for an instant — the "session" anchor's bucket key.
 *
 *  A futures session rolls at the CME open, 17:00 US Central, NOT midnight: a
 *  calendar reset would cut each session in half. The open is 22:00 UTC in US
 *  daylight time but 23:00 UTC in winter, so the fixed +2h shift this replaced
 *  bucketed every winter session an hour early — the first hour of each session
 *  was cumulated into the previous day's CVD.
 *
 *  Adding 7h maps 17:00 local to 00:00 of the next local day, so formatting the
 *  shifted instant in America/Chicago yields the trade date. The shift is
 *  applied in absolute time and the conversion is done by Intl, so DST is
 *  handled by the timezone database rather than by us. The one instant this
 *  approximation could slip — the DST changeover at 02:00 Sunday Central — is
 *  when the market is shut (Friday 16:00 close to Sunday 17:00 open), so no bar
 *  ever lands there.
 *
 *  en-CA formats as YYYY-MM-DD. The formatter is built once; constructing an
 *  Intl.DateTimeFormat per bar is slow enough to show on a 40k-bar series.
 */
const CME_TRADE_DATE = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Chicago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const sessionKey = (ms: number): string =>
  CME_TRADE_DATE.format(new Date(ms + 7 * 3600_000));

const PANE_H = 116;

/** price/OI quadrants — the interpretation, in one place. */
const QUADRANT = {
  newLongs: { color: "#26a69a", label: "price up + OI up · new longs" },
  shortCover: { color: "#80cbc4", label: "price up + OI down · short covering" },
  newShorts: { color: "#ef5350", label: "price down + OI up · new shorts" },
  longLiq: { color: "#ef9a9a", label: "price down + OI down · long liquidation" },
} as const;

interface Props {
  /** Chart tab id — also the range-sync group. */
  tabId: string;
  timeframe: string;
  /** Visible window, ISO dates, from the price chart. */
  start: string;
  end: string;
  /** Daily closes keyed by YYYY-MM-DD, for the OI quadrant colouring. */
  dailyClose: Record<string, number>;
  /** Every loaded price bar's open time, ascending.
   *
   *  The panes are rendered onto exactly this grid — one point per price bar,
   *  whitespace where there's no data. Without it the panes would carry their
   *  own bar counts (CVD ~90, OI ~1,700, price ~2,000) and logical-range sync
   *  would align *indices* rather than *times*, so the panes would scroll to a
   *  different part of history than the chart. Sharing the grid makes the two
   *  indexings identical, which is what makes the sync exact. */
  barTimes: number[];
}

/** Distribute values onto the price-bar grid.
 *
 *  For each price bar i, fold every source point whose timestamp falls in
 *  [t_i, t_i+1) into that bar. Works for any combination of source and chart
 *  timeframe — 1H deltas onto a 1D chart aggregate; a daily figure onto a 1H
 *  chart lands on one bar. Linear in both inputs; both are already sorted.
 */
function foldOntoBars<T>(
  barTimes: number[],
  points: { time: number }[],
  fold: (acc: T | null, p: never) => T,
): (T | null)[] {
  const out: (T | null)[] = new Array(barTimes.length).fill(null);
  let i = 0;
  for (let b = 0; b < barTimes.length; b += 1) {
    const next = b + 1 < barTimes.length ? barTimes[b + 1] : Infinity;
    while (i < points.length && points[i].time < barTimes[b]) i += 1;
    let acc: T | null = null;
    while (i < points.length && points[i].time < next) {
      acc = fold(acc, points[i] as never);
      i += 1;
    }
    out[b] = acc;
  }
  return out;
}

export function GCPanels({
  tabId,
  timeframe,
  start,
  end,
  dailyClose,
  barTimes,
}: Props) {
  const [contract, setContract] = useState<Contract>("GCZ6");
  const [anchor, setAnchor] = useState<Anchor>("session");
  const [delta, setDelta] = useState<GCDeltaBar[]>([]);
  const [oi, setOi] = useState<GCOIPoint[]>([]);
  const [cvdNote, setCvdNote] = useState<string>("");

  // The pane only supports the timeframes the store holds; below 1H there is no
  // GC data at all, so asking would just 400.
  const tf = ["1H", "2H", "4H"].includes(timeframe) ? timeframe : "1H";

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.gcCvd({ contract, timeframe: tf, start, end });
        if (cancelled) return;
        setDelta(res.bars);
        setCvdNote(
          res.days_available.length === 0
            ? "no tick data purchased for this range"
            : `${res.days_available.length} day(s) covered`,
        );
      } catch {
        if (!cancelled) {
          setDelta([]);
          setCvdNote("unavailable");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contract, tf, start, end]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.gcOi({ start, end });
        if (!cancelled) setOi(res.series);
      } catch {
        if (!cancelled) setOi([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [start, end]);

  /** Per-price-bar delta, then cumulated within the anchor.
   *
   *  Folding onto the price grid FIRST is what keeps the pane aligned: bar i of
   *  this series is bar i of the chart, always. Bars with no traded delta stay
   *  null (whitespace) so an un-purchased stretch draws nothing. */
  const cvd = useMemo<PanePoint[]>(() => {
    const folded = foldOntoBars<number>(
      barTimes,
      delta,
      (acc, p: { delta: number }) => (acc ?? 0) + p.delta,
    );
    let running = 0;
    let bucket = "";
    return barTimes.map((t, i) => {
      const d = folded[i];
      if (d == null) return { time: t, value: null };
      const dt = new Date(t * 1000);
      const key =
        anchor === "range"
          ? "all"
          : anchor === "day"
            ? dt.toISOString().slice(0, 10)
            : sessionKey(dt.getTime());
      if (key !== bucket) {
        bucket = key;
        running = 0;
      }
      running += d;
      return { time: t, value: running };
    });
  }, [barTimes, delta, anchor]);

  /** Absolute OI, held flat across each session's bars.
   *
   *  A settlement figure IS constant for the session it describes, so holding
   *  it is the honest rendering of daily data on an intraday axis — not
   *  interpolation. Bars before the first reading stay whitespace. */
  const oiLine = useMemo<PanePoint[]>(() => {
    const folded = foldOntoBars<number>(
      barTimes,
      oi,
      (_acc, p: { oi: number }) => p.oi,
    );
    let held: number | null = null;
    return barTimes.map((t, i) => {
      if (folded[i] != null) held = folded[i];
      return { time: t, value: held };
    });
  }, [barTimes, oi]);

  const oiChange = useMemo<PanePoint[]>(() => {
    // Colour needs the previous session's close, so resolve quadrants over the
    // OI series first, then fold the result onto the price grid.
    const coloured: { time: number; value: number; color: string }[] = [];
    let prevClose: number | null = null;
    for (const p of oi) {
      const close = dailyClose[p.date];
      if (p.change != null) {
        let color: string = QUADRANT.newLongs.color;
        if (close != null && prevClose != null) {
          const priceUp = close >= prevClose;
          const oiUp = p.change >= 0;
          color = priceUp
            ? oiUp
              ? QUADRANT.newLongs.color
              : QUADRANT.shortCover.color
            : oiUp
              ? QUADRANT.newShorts.color
              : QUADRANT.longLiq.color;
        }
        coloured.push({ time: p.time, value: p.change, color });
      }
      if (close != null) prevClose = close;
    }
    const folded = foldOntoBars<{ value: number; color: string }>(
      barTimes,
      coloured,
      (_acc, p: { value: number; color: string }) => ({
        value: p.value,
        color: p.color,
      }),
    );
    return barTimes.map((t, i) => ({
      time: t,
      value: folded[i]?.value ?? null,
      ...(folded[i] ? { color: folded[i]!.color } : {}),
    }));
  }, [barTimes, oi, dailyClose]);

  return (
    <div className={styles.panels}>
      <div className={styles.controls}>
        <span className={styles.group}>
          <span className={styles.label}>CVD</span>
          {CONTRACTS.map((c) => (
            <button
              key={c}
              type="button"
              className={c === contract ? styles.chipOn : styles.chip}
              onClick={() => setContract(c)}
              title="Contracts are independent order books — delta is never cumulated across a roll"
            >
              {c}
            </button>
          ))}
        </span>
        <span className={styles.group}>
          <span className={styles.label}>anchor</span>
          {(["session", "day", "range"] as Anchor[]).map((a) => (
            <button
              key={a}
              type="button"
              className={a === anchor ? styles.chipOn : styles.chip}
              onClick={() => setAnchor(a)}
              title="CVD is cumulated from this reset point"
            >
              {a}
            </button>
          ))}
        </span>
        <span className={styles.legend}>
          {Object.values(QUADRANT).map((q) => (
            <span key={q.label} className={styles.legendItem}>
              <i style={{ background: q.color }} />
              {q.label}
            </span>
          ))}
        </span>
      </div>

      <TimeSeriesPane
        groupId={tabId}
        paneId="cvd"
        title={`CVD · ${contract}`}
        subtitle={`${tf} · anchored per ${anchor} · ${cvdNote}`}
        data={cvd}
        kind="line"
        color="#42a5f5"
        height={PANE_H}
        zeroLine
        lastValueLabel="cvd"
        empty="No CVD here — tick data was only purchased for 23 Jun – 5 Aug 2026."
      />

      <TimeSeriesPane
        groupId={tabId}
        paneId="oi"
        title="Open Interest · all expiries"
        subtitle="settlement, daily · total across every GC contract"
        data={oiLine}
        kind="line"
        color="#ab47bc"
        height={PANE_H}
        lastValueLabel="oi"
        empty="No open interest for this range."
      />

      <TimeSeriesPane
        groupId={tabId}
        paneId="oi-change"
        title="Δ Open Interest"
        subtitle="daily change, coloured by price/OI quadrant"
        data={oiChange}
        kind="histogram"
        color={QUADRANT.newLongs.color}
        height={PANE_H}
        zeroLine
        empty="No open interest change for this range."
      />
    </div>
  );
}
